import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

const todayStr = new Date().toISOString().split('T')[0];
const currentYear = String(new Date().getFullYear());

function deriveRange(year) {
  const y = year || currentYear;
  const start = `${y}-01-01`;
  const end = y === currentYear ? todayStr : `${y}-12-31`;
  return { start, end };
}

/**
 * DateRangeExportBar
 *
 * Props:
 *   onExport(startDate, endDate, format) — called when download button is clicked
 *   title    — string label (e.g. 'Graph 1a')
 *   loading  — bool
 *   year     — selected year string from global filter; updates date range automatically
 */
const DateRangeExportBar = ({ onExport, title = '', loading = false, year = '' }) => {
  const initial = deriveRange(year || currentYear);
  const [startDate, setStartDate] = useState(initial.start);
  const [endDate, setEndDate] = useState(initial.end);

  // Sync date range whenever the year prop changes
  useEffect(() => {
    if (year) {
      const { start, end } = deriveRange(year);
      setStartDate(start);
      setEndDate(end);
    }
  }, [year]);
  const [format, setFormat] = useState('csv');

  const handleFormatChange = (_e, newFormat) => {
    if (newFormat) setFormat(newFormat);
  };

  const handleDownload = () => {
    if (onExport) {
      onExport(startDate, endDate, format);
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      flexWrap="wrap"
      mt={1}
      sx={{ borderTop: '1px solid #e0e0e0', pt: 1 }}
    >
      {title && (
        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
          {title}
        </Typography>
      )}
      <TextField
        type="date"
        label="From"
        size="small"
        value={startDate}
        onChange={(e) => setStartDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{ width: 150 }}
        disabled={loading}
      />
      <TextField
        type="date"
        label="To"
        size="small"
        value={endDate}
        onChange={(e) => setEndDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{ width: 150 }}
        disabled={loading}
      />
      <ToggleButtonGroup
        value={format}
        exclusive
        onChange={handleFormatChange}
        size="small"
        disabled={loading}
      >
        <ToggleButton value="csv" sx={{ textTransform: 'none', px: 1.5 }}>
          CSV
        </ToggleButton>
        <ToggleButton value="xlsx" sx={{ textTransform: 'none', px: 1.5 }}>
          XLSX
        </ToggleButton>
      </ToggleButtonGroup>
      <Tooltip title="Download">
        <span>
          <IconButton
            onClick={handleDownload}
            disabled={loading}
            size="small"
            sx={{ color: '#0057A8' }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default DateRangeExportBar;
